import { E as EVENTS } from './events-80a90688.js';

class ProductBlockVariantSelector extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      variantButton: "[js-variant-button]",
      variantGroup: "[js-variant-group-container]",
      variantLabelValue: "[js-variant-label-value]",
      variantOption: "[js-variant-option]",
      variantInput: "[js-variant-input]",
      noJsVariantSelector: "[js-no-js-variant-selector",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();

    // Listen for variant change
    this.initActiveVariants();

    // Reinitialize listeners on variant change
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      this.handleRepaint.bind(this),
      { signal: this.controller.signal },
    );
  }

  // TODO: This gets called a lot, see if there's a more performant solution
  initActiveVariants() {
    this.activeVariantsController = new AbortController();

    // Remove no-js variant selector to prevent conflicting id's
    this.noJsVariantSelector = this.querySelector(
      this.selectors.noJsVariantSelector,
    );
    this.noJsVariantSelector?.remove();

    this.variantInputs = this.querySelectorAll(this.selectors.variantInput);
    this.variantInputs.forEach((input) => {
      input.addEventListener("change", this.onVariantChange, {
        signal: this.activeVariantsController.signal,
      });
    });

    // Listeners for tabbing and selecting swatches and chips specifically
    this.variantButton = this.querySelectorAll(this.selectors.variantButton);
    this.variantButton.forEach((button) => {
      button.addEventListener(
        "keypress",
        (event) => {
          if (event.key === "Enter") {
            event.preventDefault();

            const swatchButton = button.getAttribute("js-swatch");
            const chipButton = button.getAttribute("js-chip");

            if (swatchButton !== null) {
              const swatchWrapper = event.target.parentElement;
              const inputHtml = swatchWrapper.previousElementSibling;

              this.onVariantChangeA11y(inputHtml);
            } else if (chipButton !== null) {
              this.onVariantChangeA11y(event.target.previousElementSibling);
            }
          }
        },
        {
          signal: this.activeVariantsController.signal,
        },
      );
    });
  }

  onVariantChange(event) {
    this.variantLayout = this.getAttribute("data-variant-layout");
    this.swatchOption = this.getAttribute("data-swatch-option");

    switch (this.variantLayout) {
      case "chips":
        // F&E: optional chaining, since no chip is selected until the shopper picks a size
        event.target.parentElement.parentElement
          .querySelector(".chip--selected")
          ?.classList.remove("chip--selected");

        event.target.parentElement
          .querySelector("[js-chip]")
          .classList.add("chip--selected");
        break

      case "swatches":
        event.target.parentElement.parentElement
          .querySelector(".swatch--selected")
          ?.classList.remove("swatch--selected");

        event.target.parentElement
          .querySelector("[js-swatch]")
          .classList.add("swatch--selected");
        break
    }

    let optionValueElement;
    if (this.variantLayout === "swatches" || this.variantLayout === "chips") {
      optionValueElement = event.target;
    } else if (this.variantLayout === "dropdown") {
      optionValueElement = event.target.selectedOptions[0];
    }

    const variantChangedEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: {
          newOptionInput: optionValueElement,
        },
      },
    );

    this.dispatchEvent(variantChangedEvent);
  }

  onVariantChangeA11y(inputHtml) {
    const variantChangedEvent = new CustomEvent(
      EVENTS.PRODUCT_VARIANT_CHANGING,
      {
        bubbles: true,
        detail: {
          newOptionInput: inputHtml,
        },
      },
    );

    this.dispatchEvent(variantChangedEvent);
  }

  handleRepaint() {
    this.activeVariantsController.abort();
    this.initActiveVariants();
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-block-variant-selector")) {
  customElements.define(
    "product-block-variant-selector",
    ProductBlockVariantSelector,
  );
}

//
// F&E: require an explicit size choice on the product page.
//
// Without this the page loads with the first available size (usually S)
// already selected and Add to Cart live, so shoppers who never touch the
// size picker end up ordering a Small.
//
// Applies only to the main product, when its single option is "Size".
// Skipped when the URL carries ?variant= so deep links keep their size.
//
function feInitSizeRequired() {
  if (window.__feSizeRequiredInit) {
    return
  }
  window.__feSizeRequiredInit = true;

  if (new URLSearchParams(window.location.search).has("variant")) {
    return
  }

  if (window.Shopify && window.Shopify.designMode) {
    return
  }

  const container = document.querySelector("main-product-default");
  if (!container) {
    return
  }

  const root = container.querySelector("product-block-variant-selector");
  if (!root) {
    return
  }

  const groups = root.querySelectorAll("[js-variant-group-container]");
  if (groups.length !== 1) {
    return
  }

  const group = groups[0];
  const optionName = (group.querySelector(".label-text")?.textContent || "")
    .replace(":", "")
    .trim()
    .toLowerCase();
  if (optionName !== "size") {
    return
  }

  const getInputs = () =>
    Array.from(root.querySelectorAll('input[type="radio"][js-variant-input]'));

  const initialInputs = getInputs();
  if (initialInputs.length < 2) {
    return
  }

  // Fully sold out product: leave the theme's default "Sold out" state alone
  if (!initialInputs.some((input) => !input.classList.contains("disabled"))) {
    return
  }

  const TEXT = {
    button: "Select a Size",
    label: "Select a size",
    error: "Please select a size",
  };

  const html = document.documentElement;
  let pending = true;

  const style = document.createElement("style");
  style.textContent = `
    html[data-fe-size-pending] main-product-default .dynamic-checkout-container { display: none !important; }
    html[data-fe-size-pending] main-product-default [js-product-block-inventory-status],
    html[data-fe-size-pending] main-product-default [js-product-block-sku] { visibility: hidden; }
    .fe-size-required__message { margin: 8px 0 0; font-size: 0.875rem; font-weight: 600; color: #b3261e; }
    .fe-size-required__message:empty { display: none; }
    .fe-size-required--error .option-group__chips-container .chip { box-shadow: 0 0 0 2px #b3261e; }
  `;
  document.head.appendChild(style);

  const message = document.createElement("p");
  message.className = "fe-size-required__message";
  message.setAttribute("role", "alert");
  group.appendChild(message);

  const getButtonLabels = () =>
    container.querySelectorAll(
      "[js-add-to-cart-button] .button-with-state__label--hidden, [js-add-to-cart-button] .label-default, [js-add-to-cart-button] .label-idle__inner",
    );

  const applyPending = () => {
    if (!pending) {
      return
    }

    html.setAttribute("data-fe-size-pending", "");

    getInputs().forEach((input) => {
      input.checked = false;
    });

    root.querySelectorAll(".chip--selected").forEach((chip) => {
      chip.classList.remove("chip--selected");
    });

    const labelValue = group.querySelector("[js-variant-label-value]");
    if (labelValue) {
      labelValue.textContent = TEXT.label;
    }

    getButtonLabels().forEach((label) => {
      if (label.dataset.feOriginal === undefined) {
        label.dataset.feOriginal = label.textContent;
      }
      label.textContent = TEXT.button;
    });
  };

  const clearPending = (input) => {
    if (!pending) {
      return
    }
    pending = false;

    html.removeAttribute("data-fe-size-pending");
    root.classList.remove("fe-size-required--error");
    message.textContent = "";

    getButtonLabels().forEach((label) => {
      if (label.dataset.feOriginal !== undefined) {
        label.textContent = label.dataset.feOriginal;
        delete label.dataset.feOriginal;
      }
    });

    if (input && input.parentElement) {
      const chip = input.parentElement.querySelector("[js-chip]");
      if (chip) {
        chip.classList.add("chip--selected");
      }

      const labelValue = group.querySelector("[js-variant-label-value]");
      if (labelValue && labelValue.textContent === TEXT.label) {
        labelValue.textContent = chip ? chip.getAttribute("aria-label") || "" : "";
      }
    }
  };

  // Any explicit size choice ends the pending state
  root.addEventListener(
    "change",
    (event) => {
      if (event.target.matches && event.target.matches("[js-variant-input]")) {
        clearPending(event.target);
      }
    },
    true,
  );

  root.addEventListener("click", (event) => {
    if (
      pending &&
      event.target.matches &&
      event.target.matches("input[js-variant-input]") &&
      event.target.checked
    ) {
      clearPending(event.target);
    }
  });

  root.addEventListener(EVENTS.PRODUCT_VARIANT_CHANGING, (event) => {
    clearPending(event.detail && event.detail.newOptionInput);
  });

  // Block add to cart until a size is chosen
  document.addEventListener(
    "submit",
    (event) => {
      if (!pending) {
        return
      }

      const form = event.target;
      if (
        !form.matches ||
        !form.matches("[data-product-form]") ||
        !container.contains(form)
      ) {
        return
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      root.classList.add("fe-size-required--error");
      message.textContent = TEXT.error;
      root.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    true,
  );

  // Browsers can restore a checked radio on reload or back navigation
  window.addEventListener("pageshow", applyPending);

  applyPending();
}

feInitSizeRequired();
